import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import {
    AuthService,
    EntityData,
    JwtHttpClientService,
    LocalisableContextData,
    provideEntityModel
} from "@fedeltapointofsale/data-client";
import {AccessRoleEntityModel} from "../access-role-entity.model";
import {Subscription} from "rxjs";
import {AccessRoleData} from "../access-role.data";
import {OutletReferenceData} from "../../../pos/outlet/outlet.data";
import {CustomObjectReferenceData} from "../../../custom-objects/custom-object/custom-object.data";
import {PrivilegeAuthorisations, PrivilegeGroup, PrivilegeGroupings} from "../privilege.data";
import {PrivilegeRecord} from "../privilege-selection/privilege-selection.component";
import {FUIResponsiveService} from "@fedeltapointofsale/ui";
import {HttpResponse} from "@angular/common/http";

/**
 * Proxy the state between the localisation wbo-edit-localisation control and the model data
 */
export class LocalisableContextProxyData extends LocalisableContextData {

    private __outlets: number[];
    private __customObjects: number[];

    constructor(private data: AccessRoleData) {
        super();

        if (this.data.outlets) {
            this.__outlets = [];
            this.data.outlets.forEach((o: OutletReferenceData) => {
                this.__outlets.push(o.outletId);
            });
        }

        if (this.data.customObjects) {
            this.__customObjects = [];
            this.data.customObjects.forEach((o: CustomObjectReferenceData) => {
                this.__customObjects.push(o.customObjectId);
            });
        }

    }

    override get universal(): boolean {
        return this.data.universal;
    }

    override set universal(b: boolean) {
        this.data.universal = b;
    }

    override get outlets(): number[] {
        return this.__outlets;
    }

    override set outlets(o: number[]) {
        this.__outlets = o;
    }

    override get customObjects(): number[] {
        return this.__customObjects;
    }

    override set customObjects(o: number[]) {
        this.__customObjects = o;
    }

    refresh(): void {
        if (this.__outlets) {
            this.data.outlets = [];
            this.__outlets.forEach((id: number) => {
                const outlet: OutletReferenceData = <OutletReferenceData>EntityData.create(OutletReferenceData);
                outlet.outletId = id;
                this.data.outlets.push(outlet);
            });
        }

        if (this.__customObjects) {
            this.data.customObjects = [];
            this.__customObjects.forEach((id: number) => {
                const co: CustomObjectReferenceData = <CustomObjectReferenceData>EntityData.create(CustomObjectReferenceData);
                co.customObjectId = id;
                this.data.customObjects.push(co);
            });
        }
    }
}

export class PrivilegeSection {
    public name: string;
    public privileges: PrivilegeRecord[];

    sortPrivs(): void {
        if (this.privileges) {
            this.privileges.sort(((a: PrivilegeRecord, b: PrivilegeRecord) => a.name.localeCompare(b.name)));
        }
    }
}

/**
 * Privileges for a specific namespace (bo, fo, wo)
 */
export class PrivilegeNamespaceGroup {
    public name: string;
    public sections: PrivilegeSection[];

    sortSections(): void {
        if (this.sections) {
            this.sections.sort(((a: PrivilegeSection, b: PrivilegeSection) => a.name.localeCompare(b.name)));
            this.sections.forEach((s: PrivilegeSection) => s.sortPrivs());
        }
    }
}

@Component({
    selector: "wbo-access-role-edit-page",
    templateUrl: "./access-role-edit-page.component.html",
    providers: [(provideEntityModel(AccessRoleEntityModel))]
})
export class AccessRoleEditPageComponent extends AbstractEditPage implements OnInit, OnDestroy {

    private static readonly PREFIX_VIEW: string = "view";
    private static readonly PREFIX_CREATE: string = "create";
    private static readonly PREFIX_UPDATE: string = "update";
    private static readonly PREFIX_DELETE: string = "delete";

    private static readonly BO: string = "bo";
    private static readonly FO: string = "fo";


    private subscriptions: Subscription = new Subscription();

    localisableContext: LocalisableContextProxyData;

    privileges: PrivilegeNamespaceGroup[];


    constructor(public model: AccessRoleEntityModel,
                private http: JwtHttpClientService,
                private authService: AuthService,
                changeDetectorRef: ChangeDetectorRef,
                responsiveService: FUIResponsiveService) {
        super(changeDetectorRef, responsiveService);
    }

    ngOnInit(): void {

        this.subscriptions.add(this.http.get("/dataserver/privileges").subscribe((privs: HttpResponse<PrivilegeGroupings[]>) => {
            this.initPrivs(privs.body);

            this.subscriptions.add(this.model.$data.subscribe(() => {
                this.localisableContext = new LocalisableContextProxyData(this.model.data);
            }));

            this.subscriptions.add(this.editPageComponent.saved.subscribe(() => {
                this.authService.refreshAccessToken().toPromise().then();
            }))

        }));

    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    selectionChanged(): void {
        this.localisableContext.refresh();
    }


    private initPrivs(privs: PrivilegeGroupings[]): void {
        this.privileges = [];

        let bo: PrivilegeNamespaceGroup;
        let fo: PrivilegeNamespaceGroup;
        const others: PrivilegeNamespaceGroup[] = [];

        privs.forEach((p: PrivilegeGroupings) => {
            const privRecordMap: Map<string, PrivilegeRecord> = new Map();
            if (p.groups) {
                const nsGroup = new PrivilegeNamespaceGroup();
                nsGroup.name = p.groupType;
                nsGroup.sections = [];
                if (p.groupType === AccessRoleEditPageComponent.BO) {
                    bo = nsGroup;
                }
                else if (p.groupType === AccessRoleEditPageComponent.FO) {
                    fo = nsGroup;
                }
                else {
                    others.push(nsGroup)
                }
                p.groups.forEach((pg: PrivilegeGroup) => {
                    const section: PrivilegeSection = new PrivilegeSection();
                    section.privileges = [];
                    nsGroup.sections.push(section);
                    pg.authorisations.forEach((pa: PrivilegeAuthorisations) => {
                        const k: string = this.stripAction(pa.name);
                        let rec: PrivilegeRecord = privRecordMap.get(k);

                        if (!rec) {
                            rec = new PrivilegeRecord();
                            rec.id = k;
                            rec.name = k;
                            rec.groupType = nsGroup.name;
                            section.privileges.push(rec);
                            section.name = pa.group;
                            privRecordMap.set(k, rec);
                        }

                        if (pa.name.startsWith(AccessRoleEditPageComponent.PREFIX_VIEW)) {
                            rec.supportView = true;
                        }
                        else if (pa.name.startsWith(AccessRoleEditPageComponent.PREFIX_CREATE)) {
                            rec.supportCreate = true;
                        }
                        else if (pa.name.startsWith(AccessRoleEditPageComponent.PREFIX_UPDATE)) {
                            rec.supportUpdate = true;
                        }
                        else if (pa.name.startsWith(AccessRoleEditPageComponent.PREFIX_DELETE)) {
                            rec.supportDelete = true;
                        }
                        else {
                            rec.supportAllow = true;
                        }
                    });
                });
            }
        });


        if (bo) {
            bo.sortSections();
            this.privileges.push(bo);
        }
        if (fo) {
            fo.sortSections();
            this.privileges.push(fo);
        }
        others.forEach((g: PrivilegeNamespaceGroup) => {
            g.sortSections();
            this.privileges.push(g)
        });

    }


    /**
     * Strips the action prefix from the privilege name
     * @param {string} privName
     * @returns {string}
     */
    private stripAction(privName: string): string {
        if (privName.startsWith(AccessRoleEditPageComponent.PREFIX_VIEW)) {
            return privName.substr(AccessRoleEditPageComponent.PREFIX_VIEW.length);
        }

        if (privName.startsWith(AccessRoleEditPageComponent.PREFIX_CREATE)) {
            return privName.substr(AccessRoleEditPageComponent.PREFIX_CREATE.length);
        }

        if (privName.startsWith(AccessRoleEditPageComponent.PREFIX_UPDATE)) {
            return privName.substr(AccessRoleEditPageComponent.PREFIX_UPDATE.length);
        }

        if (privName.startsWith(AccessRoleEditPageComponent.PREFIX_DELETE)) {
            return privName.substr(AccessRoleEditPageComponent.PREFIX_DELETE.length);
        }

        return privName;
    }


    addingAnother(previousEntity: AccessRoleData): void {
        this.model.data.authorisations = previousEntity.authorisations;
    }

}
